import {
  Modal,
  NewButton,
  NewButtonVariant,
} from "@visiblethread/react-components";
import clsx from "clsx";
import { TEMPLATES_ERROR_MESSAGE } from "components/DocStyleTemplatesDropdown";
import { DOC_SOURCE } from "constants/documents";
import { EXISTING_DOCUMENT_ACTION } from "constants/proposals";
import { modalTypes } from "constants/types";
import useOpenDocumentEditor from "hooks/proposalOutline/useOpenDocumentEditor";
import { number, shape, string } from "prop-types";
import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { hideModal } from "slices/app/actions/modals";
import { combineOutlineDocuments } from "slices/proposalOutline/actions/documentActions";
import { selectProposalOutlineSections } from "slices/proposalOutline/selectors";
import isNullish from "utils/isNullish";

import classes from "./CombineOutlineDocsModal.module.scss";
import CombinationSummary from "./components/CombinationSummary";
import SourcePicker from "./components/SourcePicker";
import StyleTemplatePicker from "./components/StyleTemplatePicker";

const CombineOutlineDocsModal = ({ owner }) => {
  const dispatch = useDispatch();
  const outlineSections = useSelector(selectProposalOutlineSections);

  const [currentStep, setCurrentStep] = useState(1);
  const [detachDocuments, setDetachDocuments] = useState(true);
  const [source, setSource] = useState();
  const [templateId, setTemplateId] = useState(undefined);
  const [templateError, setTemplateError] = useState();
  const openDocumentEditor = useOpenDocumentEditor();

  const hasNoTemplateId = isNullish(templateId);
  const showSourcePicker = currentStep === 2;

  const totalSections = outlineSections?.length || 0;

  const linkedDocumentIds = useMemo(() => {
    return (outlineSections || []).reduce((arr, section) => {
      const { nextgenDocumentId } = section;

      return !isNullish(nextgenDocumentId) && !arr.includes(nextgenDocumentId)
        ? arr.concat(nextgenDocumentId)
        : arr;
    }, []);
  }, [outlineSections]);

  const nextButtonText = useMemo(() => {
    switch (true) {
      case currentStep === 3:
      case currentStep === 2 && source === DOC_SOURCE.VT_EDITOR:
        return "Generate";
      default:
        return "Next";
    }
  }, [currentStep, source]);

  const onChangeDetach = () => {
    setDetachDocuments((s) => !s);
  };

  const onDismiss = () => {
    if (currentStep > 1) {
      setCurrentStep((s) => Math.max(1, s - 1));
    } else {
      dispatch(hideModal(modalTypes.COMBINE_OUTLINE_DOCS_MODAL));
    }
  };

  const onGenerate = async () => {
    if (!owner) {
      return;
    }

    const { ownerType, ownerId } = owner;
    const existingDocumentAction = detachDocuments
      ? EXISTING_DOCUMENT_ACTION.DETACH_AND_RENAME
      : EXISTING_DOCUMENT_ACTION.DELETE;

    const rsp = await dispatch(
      combineOutlineDocuments(ownerType, ownerId, {
        existingDocumentAction,
        source,
      })
    );

    if (rsp) {
      const modalType = modalTypes.COMBINE_OUTLINE_DOCS_MODAL;
      openDocumentEditor(rsp, { modalType, source, templateId });
    }
  };

  const onClickNext = () => {
    if (currentStep === 3 && hasNoTemplateId) {
      setTemplateError(TEMPLATES_ERROR_MESSAGE);
      return;
    }

    if (
      currentStep === 3 ||
      (currentStep === 2 && source === DOC_SOURCE.VT_EDITOR)
    ) {
      onGenerate();
      return;
    }

    setCurrentStep((s) => Math.min(3, s + 1));
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 2:
        return <SourcePicker onChangeSource={setSource} source={source} />;
      case 3:
        return (
          <StyleTemplatePicker
            onChangeTemplateId={setTemplateId}
            onTemplateError={setTemplateError}
            templateError={templateError}
            templateId={templateId}
          />
        );
      default:
        return (
          <CombinationSummary
            detachDocuments={detachDocuments}
            linkedDocumentIds={linkedDocumentIds}
            onChangeDetach={onChangeDetach}
            totalSections={totalSections}
          />
        );
    }
  };

  return (
    <Modal
      data-testid="combine-outline-docs-modal"
      className={clsx(
        "fade-in",
        classes.combine,
        currentStep > 1 && classes.pickers
      )}
      show
      centered
      backdrop
      onDismiss={onDismiss}
    >
      <Modal.Header>Combine Into One Document</Modal.Header>

      <Modal.Body className={classes.body}>{renderCurrentStep()}</Modal.Body>

      <Modal.Footer>
        <span className={classes.step}>Step {currentStep}</span>

        <NewButton variant={NewButtonVariant.l3} onClick={onDismiss}>
          {currentStep > 1 ? "Back" : "Cancel"}
        </NewButton>

        <NewButton
          disabled={showSourcePicker && isNullish(source)}
          variant={NewButtonVariant.l1}
          onClick={onClickNext}
        >
          {nextButtonText}
        </NewButton>
      </Modal.Footer>
    </Modal>
  );
};

CombineOutlineDocsModal.propTypes = {
  owner: shape({
    ownerType: string,
    ownerId: number,
  }),
};

export default CombineOutlineDocsModal;
